import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Loader2, X, Check, Baby } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Silence detection configuration
const SILENCE_THRESHOLD = 0.01;
const SILENCE_DURATION = 2500; // 2.5 seconds of silence before auto-stop
const MIN_RECORDING_DURATION = 1000; // Minimum 1 second recording

const BirthRecordVoiceRx = ({ 
  onEntitiesExtracted,
  babyContext,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [parsedResult, setParsedResult] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isRecordingRef = useRef(false);

  // Keep isRecordingRef in sync with isRecording state
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const stopAllAudio = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    if (audioContextRef.current) audioContextRef.current.close();
  }, []);

  useEffect(() => {
    return () => stopAllAudio();
  }, [stopAllAudio]);

  const processAudio = useCallback(async () => {
    setIsProcessing(true);
    setProcessingStep('Transcribing...');
    
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const base64Audio = reader.result.split(',')[1];
          
          // Step 1: Transcribe
          const transcribeResponse = await axios.post(`${API}/api/ai-clinical/transcribe`, {
            audio_base64: base64Audio,
            mime_type: 'audio/webm',
            language: null,
            translate_to_english: true
          });
          
          const transcript = transcribeResponse.data.text;
          
          if (!transcribeResponse.data.success || !transcript || transcript.trim() === '') {
            toast.error('Could not transcribe audio. Please try again.');
            setIsProcessing(false);
            return;
          }
          
          // Step 2: Extract birth record entities with retry
          setProcessingStep('Extracting info...');
          let extractResponse;
          let retryCount = 0;
          const maxRetries = 2;
          
          while (retryCount <= maxRetries) {
            try {
              extractResponse = await axios.post(`${API}/api/ai-clinical/extract-birth-record-entities`, {
                text: transcript,
                context: babyContext
              }, {
                timeout: 30000 // 30 second timeout
              });
              break; // Success, exit retry loop
            } catch (extractError) {
              retryCount++;
              console.warn(`Birth Record Extraction attempt ${retryCount} failed:`, extractError.message);
              if (retryCount > maxRetries) {
                throw extractError; // All retries failed
              }
              setProcessingStep(`Retrying... (${retryCount}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
            }
          }
          
          const result = extractResponse.data;
          console.log('Birth Record Voice RX - Transcription:', transcript);
          console.log('Birth Record Voice RX - Extraction result:', result);
          result.raw_text = transcript;
          setParsedResult(result);
          setProcessingStep('');
          setIsProcessing(false);
          
          // Check if any fields were extracted
          const extractedFields = Object.keys(result).filter(
            key => key !== 'raw_text' && key !== 'error' && result[key]
          );
          if (extractedFields.length > 0) {
            toast.success(`Birth record info extracted! (${extractedFields.length} fields)`);
          } else {
            toast.warning('No birth record fields detected. Try speaking clearly with field names like "antenatal history", "birth history"');
          }
        } catch (innerError) {
          console.error('Error in transcription/extraction:', innerError);
          const errorMsg = innerError.response?.data?.detail || innerError.message || 'Unknown error';
          toast.error(`Failed: ${errorMsg}`);
          setIsProcessing(false);
          setProcessingStep('');
        }
      };
      
      reader.readAsDataURL(audioBlob);
      
    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error('Failed to process dictation. Please try again.');
      setIsProcessing(false);
      setProcessingStep('');
    }
  }, [babyContext]);

  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
  }, []);

  const monitorAudioLevelRef = useRef(null);

  useEffect(() => {
    monitorAudioLevelRef.current = () => {
      if (!analyserRef.current) return;
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const value = (dataArray[i] - 128) / 128;
        sum += value * value;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      setAudioLevel(rms);
      const currentlySilent = rms < SILENCE_THRESHOLD;
      
      if (isRecordingRef.current) {
        if (currentlySilent) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              const recordingDuration = Date.now() - recordingStartTimeRef.current;
              if (recordingDuration >= MIN_RECORDING_DURATION && audioChunksRef.current.length > 0) {
                stopRecording();
              }
            }, SILENCE_DURATION);
          }
        } else {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(monitorAudioLevelRef.current);
    };
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          await processAudio();
        }
      };
      
      mediaRecorder.start(100);
      recordingStartTimeRef.current = Date.now();
      setIsRecording(true);
      if (monitorAudioLevelRef.current) {
        monitorAudioLevelRef.current();
      }
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  }, [processAudio]);

  const applyResults = () => {
    if (!parsedResult) return;
    
    // Log what we're applying
    console.log('Birth Record Voice RX - Applying results:', parsedResult);
    
    if (onEntitiesExtracted) {
      onEntitiesExtracted(parsedResult);
      
      // Count how many fields were extracted
      const extractedFields = Object.keys(parsedResult).filter(
        key => key !== 'raw_text' && key !== 'error' && parsedResult[key]
      );
      console.log('Birth Record Voice RX - Extracted fields:', extractedFields);
      toast.success(`${extractedFields.length} field(s) extracted and applied`);
    }
    
    // Close and reset
    setParsedResult(null);
    setIsOpen(false);
  };

  if (disabled) return null;

  return (
    <div className="relative">
      {/* Main Dictation Button */}
      <Button
        type="button"
        variant={isOpen ? "default" : "outline"}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 ${isOpen ? 'bg-pink-600 hover:bg-pink-700 text-white' : 'border-pink-300 text-pink-700 hover:bg-pink-50'}`}
        data-testid="birth-record-voice-rx-btn"
      >
        <Mic className="w-4 h-4" />
        <span className="hidden sm:inline">Voice Rx</span>
      </Button>
      
      {/* Dictation Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-600 to-rose-600 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Baby className="w-5 h-5" />
                <span className="font-semibold">Birth Record Voice Rx</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsOpen(false); setParsedResult(null); }}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-pink-200 mt-1">
              Dictate birth details, parent names, history
            </p>
          </div>
          
          {/* Recording Area */}
          <div className="p-4">
            {!parsedResult ? (
              <div className="text-center">
                {/* Recording Button */}
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all mx-auto ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : isProcessing 
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-pink-600 hover:bg-pink-700'
                  }`}
                  data-testid="birth-record-voice-record-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-8 h-8 text-white" />
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                </button>
                
                {/* Status Text */}
                <p className="mt-3 text-sm text-gray-600">
                  {isProcessing ? processingStep : isRecording ? 'Listening... (stops on silence)' : 'Tap to start dictating'}
                </p>
                
                {/* Audio Level Indicator */}
                {isRecording && (
                  <div className="mt-3 flex justify-center gap-1">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 rounded-full transition-all ${
                          audioLevel * 50 > i ? 'bg-pink-500' : 'bg-gray-200'
                        }`}
                        style={{ height: `${10 + i * 3}px` }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Example Phrases */}
                {!isRecording && !isProcessing && (
                  <div className="mt-4 text-xs text-gray-500">
                    <p className="font-medium mb-1">Example phrases:</p>
                    <p className="italic">&quot;Normal delivery, birth weight 3.2 kg, length 50 cm&quot;</p>
                    <p className="italic mt-1">&quot;Mother Priya, father Rahul, blood group B positive&quot;</p>
                  </div>
                )}
              </div>
            ) : (
              /* Parsed Results */
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="flex items-center justify-between sticky top-0 bg-white pb-1">
                  <span className="text-sm font-medium text-gray-700">Extracted Information</span>
                  <Badge variant="outline" className="text-xs">
                    {Object.keys(parsedResult).filter(k => k !== 'raw_text' && k !== 'error' && parsedResult[k]).length} fields
                  </Badge>
                </div>
                
                {/* Show error if extraction failed */}
                {parsedResult.error && (
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-red-600 font-medium">Could not extract birth record fields</p>
                    <p className="text-xs text-gray-500 mt-1">Please try again with clearer dictation</p>
                  </div>
                )}
                
                {/* Show message if no fields were extracted */}
                {!parsedResult.error && Object.keys(parsedResult).filter(k => k !== 'raw_text' && k !== 'error' && parsedResult[k]).length === 0 && (
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-yellow-700 font-medium">No birth record fields detected</p>
                    <p className="text-xs text-gray-500 mt-1">Try saying: "Normal delivery, baby boy, weight 3.2 kg, mother Priya"</p>
                  </div>
                )}
                
                {/* Basic Info */}
                {(parsedResult.sex || parsedResult.mode_of_delivery) && (
                  <div className="bg-pink-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Baby className="w-3 h-3 text-pink-600" />
                      <span className="text-xs font-medium text-pink-800">Basic Info</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {parsedResult.sex && (
                        <div className="text-gray-700">Sex: <span className="font-medium">{parsedResult.sex}</span></div>
                      )}
                      {parsedResult.mode_of_delivery && (
                        <div className="text-gray-700">Delivery: <span className="font-medium">{parsedResult.mode_of_delivery}</span></div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Measurements */}
                {(parsedResult.birth_weight || parsedResult.birth_length || parsedResult.head_circumference) && (
                  <div className="bg-blue-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="text-xs font-medium text-blue-800">Measurements</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      {parsedResult.birth_weight && (
                        <div className="text-gray-700">Weight: <span className="font-medium">{parsedResult.birth_weight} kg</span></div>
                      )}
                      {parsedResult.birth_length && (
                        <div className="text-gray-700">Length: <span className="font-medium">{parsedResult.birth_length} cm</span></div>
                      )}
                      {parsedResult.head_circumference && (
                        <div className="text-gray-700">HC: <span className="font-medium">{parsedResult.head_circumference} cm</span></div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Blood Group */}
                {parsedResult.blood_group && (
                  <div className="bg-red-50 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C7.589 2 4 6.135 4 11c0 4.865 6.64 10.3 7.7 11.1a.5.5 0 00.6 0C13.36 21.3 20 15.865 20 11c0-4.865-3.589-9-8-9z"/>
                      </svg>
                      <span className="text-xs font-medium text-red-800">Blood Group</span>
                      <span className="ml-auto text-sm font-bold text-red-700">{parsedResult.blood_group}</span>
                    </div>
                  </div>
                )}
                
                {/* Parents */}
                {(parsedResult.mother_name || parsedResult.father_name) && (
                  <div className="bg-purple-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs font-medium text-purple-800">Parents</span>
                    </div>
                    <div className="text-xs space-y-1">
                      {parsedResult.mother_name && (
                        <div className="text-gray-700">Mother: <span className="font-medium">{parsedResult.mother_name}</span></div>
                      )}
                      {parsedResult.father_name && (
                        <div className="text-gray-700">Father: <span className="font-medium">{parsedResult.father_name}</span></div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Antenatal History - Separate Card */}
                {parsedResult.antenatal_history && (
                  <div className="bg-indigo-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="text-xs font-medium text-indigo-800">Antenatal History</span>
                    </div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{parsedResult.antenatal_history}</p>
                  </div>
                )}
                
                {/* Birth History - Separate Card */}
                {parsedResult.birth_history && (
                  <div className="bg-cyan-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xs font-medium text-cyan-800">Birth History</span>
                    </div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{parsedResult.birth_history}</p>
                  </div>
                )}
                
                {/* Family History - Separate Card */}
                {parsedResult.family_history && (
                  <div className="bg-green-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-xs font-medium text-green-800">Family History</span>
                    </div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{parsedResult.family_history}</p>
                  </div>
                )}
                
                {/* Allergies */}
                {parsedResult.allergies && (
                  <div className="bg-yellow-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-xs font-medium text-yellow-800">Allergies</span>
                    </div>
                    <p className="text-xs text-gray-700">{parsedResult.allergies}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setParsedResult(null)}
                    className="flex-1"
                    data-testid="birth-record-voice-discard-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Discard
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={applyResults}
                    className="flex-1 bg-pink-600 hover:bg-pink-700"
                    data-testid="birth-record-voice-apply-btn"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthRecordVoiceRx;
